import translations from '../../../../../../../../translations';
import type {
    ComponentConfigurationOptions,
    ComposerElement,
} from '../../../../../../../../types/pageEditor';
import type { ValueElementIdReferenceAPI } from '../../../../../../../../types/value';
import { isNullOrEmpty } from '../../../../../../../../utils/guard';
import { useFeatureFlag } from '../../../../../../../featureFlags/FeatureFlagProvider';
import AccountDropdown from '../../../../../../../generic/AccountDropdown';
import AgentDropdown from '../../../../../../../generic/AgentDropdown';
import FormGroup from '../../../../../../../generic/FormGroup';
import HTMLInput from '../../../../../../../generic/HTMLEditor/HTMLInput';
import ValueSelectorModal from '../../../../../../../values/selector/ValueSelectorModal';
import { COMPONENT_CONFIGURATION_LABELS as componentConfigurationLabels } from '../../../../../../constants';
import { usePageEditor } from '../../../../../PageEditorProvider';
import ConfigSection from '../../ConfigSection';
import DropdownWithValuePicker from './DropdownWithValuePicker';
import InputWithValuePicker from './InputWithValuePicker';
import { useEffect } from 'react';

interface Props {
    generalOptions: { [key: string]: boolean };
    generalValues: Partial<ComposerElement>;
    requiredConfiguration: ComponentConfigurationOptions;
    updateName: (name: string) => void;
    updateLabel: (label: string) => void;
    updateContent: (content: string) => void;
    updateImageUri: (imageUri: string) => void;
    updateSource: (source: string | ValueElementIdReferenceAPI | null) => void;
    updateTarget: (source: string | ValueElementIdReferenceAPI | null) => void;
    updateFilename: (filename: string | ValueElementIdReferenceAPI | null) => void;
    updateWidth: (width: number) => void;
    updateHeight: (height: number) => void;
    updatePaymentCardCardNumberInputLabel: (
        cardNumberLabel: string | ValueElementIdReferenceAPI | null,
    ) => void;
    updatePaymentCardExpiryDateInputLabel: (
        expiryDateLabel: string | ValueElementIdReferenceAPI | null,
    ) => void;
    updatePaymentCardSecurityCodeInputLabel: (
        securityCodeLabel: string | ValueElementIdReferenceAPI | null,
    ) => void;
    updatePaymentCardPostalCodeInputLabel: (
        postalCodeLabel: string | ValueElementIdReferenceAPI | null,
    ) => void;
    updatePaymentCardNameOnCardInputLabel: (
        cardNameLabel: string | ValueElementIdReferenceAPI | null,
    ) => void;
    updateListType: (ordered: boolean) => void;
    updatePageSize: (pageSize: string | ValueElementIdReferenceAPI | null) => void;
    updateNoResultsMessage: (noResultsMessage: string | ValueElementIdReferenceAPI | null) => void;
    updateAssistantLabel: (assistantLabel: string | ValueElementIdReferenceAPI | null) => void;
    updateUserLabel: (userLabel: string | ValueElementIdReferenceAPI | null) => void;
    updateStep: (step: string | ValueElementIdReferenceAPI | null) => void;
    updateIsSlider: (isSlider: string | ValueElementIdReferenceAPI | null) => void;
    updatePaginationButtons: (paginationButtons: boolean) => void;
    updateAccountId: (accountId: string | null) => void;
    updateAgentId: (agentId: string | ValueElementIdReferenceAPI | null) => void;
    updateSessionId: (sessionId: string | ValueElementIdReferenceAPI | null) => void;
    updateMask: (mask: string | ValueElementIdReferenceAPI | null) => void;
    updateVoiceInput: (voiceInput: string | null) => void;
    updateSearchTerm: (searchTerm: string | ValueElementIdReferenceAPI | null) => void;
    updateAlertRole: (alertRole: string | null) => void;
}

const GeneralSection = ({
    generalOptions,
    generalValues,
    requiredConfiguration,
    updateName,
    updateLabel,
    updateContent,
    updateImageUri,
    updateSource,
    updateTarget,
    updateWidth,
    updateHeight,
    updateFilename,
    updatePaymentCardCardNumberInputLabel,
    updatePaymentCardExpiryDateInputLabel,
    updatePaymentCardSecurityCodeInputLabel,
    updatePaymentCardPostalCodeInputLabel,
    updatePaymentCardNameOnCardInputLabel,
    updateListType,
    updatePageSize,
    updateNoResultsMessage,
    updateAssistantLabel,
    updateUserLabel,
    updateStep,
    updateIsSlider,
    updatePaginationButtons,
    updateAccountId,
    updateAgentId,
    updateSessionId,
    updateMask,
    updateVoiceInput,
    updateSearchTerm,
    updateAlertRole,
}: Props) => {
    const { getFlag } = useFeatureFlag();
    const { container } = usePageEditor();

    // Migrate legacy attribute values to new attribute names
    useEffect(() => {
        const attributes = generalValues.attributes;
        if (!attributes) return;

        // Migrate paginationSize to $pageSize if $pageSize doesn't exist
        if (attributes['paginationSize'] && !attributes['$pageSize']) {
            updatePageSize(attributes['paginationSize']);
        }

        // Migrate noresults to $noResultsMessage if $noResultsMessage doesn't exist
        if (attributes['noresults'] && !attributes['$noResultsMessage']) {
            updateNoResultsMessage(attributes['noresults']);
        }

        // Migrate ordered to $ordered if $ordered doesn't exist
        if (attributes['ordered'] && !attributes['$ordered']) {
            updateListType(attributes['ordered'] === 'true');
        }

        // Migrate assistantLabel to $assistantLabel if $assistantLabel doesn't exist
        if (attributes['assistantLabel'] && !attributes['$assistantLabel']) {
            updateAssistantLabel(attributes['assistantLabel']);
        }

        // Migrate userLabel to $userLabel if $userLabel doesn't exist
        if (attributes['userLabel'] && !attributes['$userLabel']) {
            updateUserLabel(attributes['userLabel']);
        }
    }, [
        generalValues.attributes,
        updatePageSize,
        updateNoResultsMessage,
        updateListType,
        updateAssistantLabel,
        updateUserLabel,
    ]);

    if (!(generalOptions || generalValues)) {
        return null;
    }

    const { developerName, label, content, imageUri, width, height, attributes } = generalValues;

    const {
        hasName,
        hasLabel,
        hasContent,
        hasImageUri,
        hasWidth,
        hasHeight,
        hasSource,
        hasFilename,
        hasTarget,
        hasPaymentCardCardNumberInputLabel,
        hasPaymentCardExpiryDateInputLabel,
        hasPaymentCardSecurityCodeInputLabel,
        hasPaymentCardPostalCodeInputLabel,
        hasPaymentCardNameOnCardInputLabel,
        hasListType,
        hasPageSize,
        hasNoResultsMessage,
        hasSearchTerm,
        hasAssistantLabel,
        hasUserLabel,
        hasStep,
        hasIsSlider,
        hasPaginationButtons,
        hasAccountId,
        hasAgentId,
        hasSessionId,
        hasMask,
        hasVoiceInput,
        hasAlertRole,
    } = generalOptions ?? {};

    const {
        isNameRequired,
        isLabelRequired,
        isContentRequired,
        isImageUriRequired,
        isSourceRequired,
        isWidthRequired,
        isHeightRequired,
        isFilenameRequired,
        isTargetRequired,
    } = requiredConfiguration;

    const source = attributes?.['$src'];
    const target = attributes?.['$target'];
    const filename = attributes?.['$filename'];
    const paymentCardCardNumberInputLabel = attributes?.['$paymentCardCardNumberInputLabel'];
    const paymentCardExpiryDateInputLabel = attributes?.['$paymentCardExpiryDateInputLabel'];
    const paymentCardSecurityCodeInputLabel = attributes?.['$paymentCardSecurityCodeInputLabel'];
    const paymentCardPostalCodeInputLabel = attributes?.['$paymentCardPostalCodeInputLabel'];
    const paymentCardNameOnCardInputLabel = attributes?.['$paymentCardNameOnCardInputLabel'];
    const isOrdered = attributes?.['$ordered'] === 'true' || attributes?.['ordered'] === 'true';
    const pageSize = attributes?.['$pageSize'];
    const noResultsMessage = attributes?.['$noResultsMessage'];
    const searchTerm = attributes?.['$searchTerm'];
    const assistantLabel = attributes?.['$assistantLabel'];
    const userLabel = attributes?.['$userLabel'];
    const step = attributes?.['$step'];
    const isSlider = attributes?.['$isSlider'] === 'true';
    const paginationButtons = attributes?.['$paginationButtons'] === 'true';
    const accountId = attributes?.['$accountId'];
    const agentId = attributes?.['$agentId'];
    const sessionId = attributes?.['$sessionId'];
    const mask = attributes?.['$mask'];
    const voiceInput = attributes?.['$voiceInput'] === 'true';
    const alertRole = attributes?.['$alertRole'] === 'true';

    return (
        <ConfigSection dataTestId="general-section">
            {hasName && (
                <FormGroup
                    label={componentConfigurationLabels['NAME']}
                    htmlFor="component-name"
                    isRequired={isNameRequired}
                    isValid={!isNullOrEmpty(developerName)}
                    validationMessage={translations.PAGE_BUILDER_name_field_validation_message}
                    showValidation={true}
                >
                    <input
                        className="form-control"
                        id="component-name"
                        type="text"
                        value={developerName || ''}
                        onChange={(event) => updateName(event.target.value)}
                        title="Component name"
                        required={isNameRequired}
                    />
                </FormGroup>
            )}

            {hasLabel && (
                <FormGroup
                    label={componentConfigurationLabels['LABEL']}
                    htmlFor="component-label"
                    isRequired={isLabelRequired}
                >
                    <input
                        className="form-control"
                        id="component-label"
                        type="text"
                        value={label || ''}
                        onChange={(event) => {
                            updateLabel(event.target.value);
                        }}
                        title="Component label"
                        required={isLabelRequired}
                    />
                </FormGroup>
            )}

            {hasContent && (
                <FormGroup
                    label={componentConfigurationLabels['CONTENT']}
                    htmlFor="component-content"
                    isRequired={isContentRequired}
                >
                    <HTMLInput
                        contentValue={content || ''}
                        onChange={(userContent) => updateContent(userContent)}
                        hasLinkPlugin={true}
                        hasImagePlugin={true}
                        hasValuePickerPlugin={true}
                        height={300}
                    />
                </FormGroup>
            )}

            {hasImageUri && (
                <FormGroup
                    label={componentConfigurationLabels['IMAGE_URI']}
                    htmlFor="component-imageuri"
                    isRequired={isImageUriRequired}
                >
                    <input
                        className="form-control"
                        id="component-imageuri"
                        type="text"
                        value={imageUri || ''}
                        onChange={(event) => {
                            updateImageUri(event.target.value);
                        }}
                        title="Component image URI"
                        required={isImageUriRequired}
                    />
                </FormGroup>
            )}

            {hasSource && (
                <InputWithValuePicker
                    id="component-source"
                    label={componentConfigurationLabels['SOURCE']}
                    isRequired={isSourceRequired}
                    testId="source-configuration"
                    value={source}
                    onChangeAsValueReference={updateSource}
                    placeholder="https://boomi.com"
                    title="Source URL"
                    container={container}
                />
            )}

            {hasFilename && (
                <InputWithValuePicker
                    id="component-filename"
                    label={componentConfigurationLabels['FILENAME']}
                    isRequired={isFilenameRequired}
                    testId="filename-configuration"
                    value={filename}
                    onChangeAsValueReference={updateFilename}
                    placeholder="my-file.csv"
                    title="Name of the file"
                    container={container}
                    description={translations.PAGE_BUILDER_filename_field_description}
                />
            )}

            {hasTarget && (
                <DropdownWithValuePicker
                    id="component-target"
                    label={componentConfigurationLabels['TARGET']}
                    isRequired={isTargetRequired}
                    testId="target-configuration"
                    value={target}
                    onChangeAsValueReference={updateTarget}
                    options={[
                        {
                            label: translations.PAGE_BUILDER_target_field_new_tab_option_label,
                            value: '_blank',
                        },
                        {
                            label: translations.PAGE_BUILDER_target_field_same_tab_option_label,
                            value: '_self',
                        },
                    ]}
                    defaultValue="_blank"
                    title={componentConfigurationLabels['TARGET']}
                    container={container}
                    description={translations.PAGE_BUILDER_target_field_description}
                />
            )}

            {hasPaymentCardCardNumberInputLabel && (
                <InputWithValuePicker
                    id="component-card-number"
                    label={componentConfigurationLabels['PAYMENT_CARD_CARD_NUMBER_LABEL']}
                    testId="card-number-configuration"
                    value={paymentCardCardNumberInputLabel}
                    onChangeAsValueReference={updatePaymentCardCardNumberInputLabel}
                    placeholder="Card number"
                    title="Credit card number"
                    container={container}
                />
            )}

            {hasPaymentCardExpiryDateInputLabel && (
                <InputWithValuePicker
                    id="component-expiry-date"
                    label={componentConfigurationLabels['PAYMENT_CARD_EXPIRY_DATE_LABEL']}
                    testId="expiry-date-configuration"
                    value={paymentCardExpiryDateInputLabel}
                    onChangeAsValueReference={updatePaymentCardExpiryDateInputLabel}
                    placeholder="Expiry date"
                    title="Card expiration date"
                    container={container}
                />
            )}

            {hasPaymentCardSecurityCodeInputLabel && (
                <InputWithValuePicker
                    id="component-security-code"
                    label={componentConfigurationLabels['PAYMENT_CARD_SECURITY_CODE_LABEL']}
                    testId="security-code-configuration"
                    value={paymentCardSecurityCodeInputLabel}
                    onChangeAsValueReference={updatePaymentCardSecurityCodeInputLabel}
                    placeholder="Security code"
                    title="Card security code"
                    container={container}
                />
            )}

            {hasPaymentCardPostalCodeInputLabel && (
                <InputWithValuePicker
                    id="component-postal-code"
                    label={componentConfigurationLabels['PAYMENT_CARD_POSTAL_CODE_LABEL']}
                    testId="postal-code-configuration"
                    value={paymentCardPostalCodeInputLabel}
                    onChangeAsValueReference={updatePaymentCardPostalCodeInputLabel}
                    placeholder="Postal code"
                    title="Billing postal code"
                    container={container}
                />
            )}

            {hasPaymentCardNameOnCardInputLabel && (
                <InputWithValuePicker
                    id="component-name-on-card"
                    label={componentConfigurationLabels['PAYMENT_CARD_NAME_ON_CARD_LABEL']}
                    testId="name-on-card-configuration"
                    value={paymentCardNameOnCardInputLabel}
                    onChangeAsValueReference={updatePaymentCardNameOnCardInputLabel}
                    placeholder="Name on card"
                    title="Cardholder name"
                    container={container}
                />
            )}

            {hasWidth && (
                <FormGroup
                    label={componentConfigurationLabels['WIDTH']}
                    htmlFor="component-width"
                    isRequired={isWidthRequired}
                >
                    <input
                        className="form-control"
                        id="component-width"
                        type="number"
                        value={width || ''}
                        onChange={(event) => {
                            updateWidth(
                                Number.isNaN(event.target.valueAsNumber)
                                    ? 0
                                    : event.target.valueAsNumber,
                            );
                        }}
                        title="Component width"
                        required={isWidthRequired}
                    />
                </FormGroup>
            )}

            {hasHeight && (
                <FormGroup
                    label={componentConfigurationLabels['HEIGHT']}
                    htmlFor="component-height"
                    isRequired={isHeightRequired}
                >
                    <input
                        className="form-control"
                        id="component-height"
                        type="number"
                        value={height || ''}
                        onChange={(event) => {
                            updateHeight(
                                Number.isNaN(event.target.valueAsNumber)
                                    ? 0
                                    : event.target.valueAsNumber,
                            );
                        }}
                        title="Component height"
                        required={isHeightRequired}
                    />
                </FormGroup>
            )}

            {hasListType && (
                <div className="radio-button-group horizontal">
                    <label className="radio-button-label">
                        <input
                            className="radio-button-input"
                            type="radio"
                            id={componentConfigurationLabels['BULLETED_LIST_TYPE']}
                            value={componentConfigurationLabels['BULLETED_LIST_TYPE']}
                            checked={!isOrdered}
                            onChange={() => updateListType(false)}
                        />
                        {componentConfigurationLabels['BULLETED_LIST_TYPE']}
                    </label>
                    <label className="radio-button-label">
                        <input
                            className="radio-button-input"
                            type="radio"
                            id={componentConfigurationLabels['NUMBERED_LIST_TYPE']}
                            value={componentConfigurationLabels['NUMBERED_LIST_TYPE']}
                            checked={isOrdered}
                            onChange={() => updateListType(true)}
                        />
                        {componentConfigurationLabels['NUMBERED_LIST_TYPE']}
                    </label>
                </div>
            )}

            {hasPaginationButtons && (
                <div className="radio-button-group horizontal">
                    <label className="radio-button-label">
                        <input
                            className="radio-button-input"
                            type="radio"
                            id={componentConfigurationLabels['SCROLL_LOADING']}
                            value={componentConfigurationLabels['SCROLL_LOADING']}
                            checked={!paginationButtons}
                            onChange={() => updatePaginationButtons(false)}
                        />
                        {componentConfigurationLabels['SCROLL_LOADING']}
                    </label>
                    <label className="radio-button-label">
                        <input
                            className="radio-button-input"
                            type="radio"
                            id={componentConfigurationLabels['BUTTON_PAGINATION']}
                            value={componentConfigurationLabels['BUTTON_PAGINATION']}
                            checked={paginationButtons}
                            onChange={() => updatePaginationButtons(true)}
                        />
                        {componentConfigurationLabels['BUTTON_PAGINATION']}
                    </label>
                </div>
            )}

            {hasPageSize && (
                <InputWithValuePicker
                    id="component-page-size"
                    label={componentConfigurationLabels['PAGE_SIZE']}
                    testId="page-size-configuration"
                    value={pageSize}
                    onChangeAsValueReference={updatePageSize}
                    title="Number of items to load per page"
                    container={container}
                    type="number"
                />
            )}

            {hasNoResultsMessage && (
                <InputWithValuePicker
                    id="component-no-results-message"
                    label={componentConfigurationLabels['NO_RESULTS_MESSAGE']}
                    testId="no-results-message-configuration"
                    value={noResultsMessage}
                    onChangeAsValueReference={updateNoResultsMessage}
                    placeholder="No results found"
                    title="Message to display when no results are found"
                    container={container}
                />
            )}

            {hasSearchTerm && getFlag('WRIATT') && (
                <InputWithValuePicker
                    id="component-search-term"
                    label={componentConfigurationLabels['SEARCH_TERM']}
                    testId="search-term-configuration"
                    value={searchTerm}
                    onChangeAsValueReference={updateSearchTerm}
                    title="Pre-populate the search input with a value or bind to a Flow Value"
                    container={container}
                />
            )}

            {hasAccountId && getFlag('AGTSTP') && (
                <AccountDropdown
                    label={translations.AGENT_account_label}
                    isRequired={false}
                    value={accountId as string}
                    onChange={updateAccountId}
                />
            )}

            {hasAgentId && getFlag('AGTSTP') && (
                <AgentDropdown
                    accountId={accountId as string}
                    label={translations.AGENT_agent_label}
                    isRequired={false}
                    value={(agentId as string) || null}
                    onChange={(agent) => updateAgentId(agent?.id || null)}
                />
            )}

            {hasSessionId && getFlag('AGTSTP') && (
                <FormGroup
                    label={componentConfigurationLabels['SESSION_ID']}
                    htmlFor="session-id"
                    isRequired={false}
                >
                    <ValueSelectorModal
                        value={sessionId as ValueElementIdReferenceAPI}
                        onChangeAsValueReference={updateSessionId}
                        contentType={'ContentString'}
                        includeSystemValues={false}
                        container={container}
                    />
                </FormGroup>
            )}

            {hasAssistantLabel && (
                <InputWithValuePicker
                    id="component-assistant-label"
                    label={componentConfigurationLabels['ASSISTANT_LABEL']}
                    testId="assistant-label-configuration"
                    value={assistantLabel}
                    onChangeAsValueReference={updateAssistantLabel}
                    title="Label for the assistant"
                    container={container}
                />
            )}

            {hasUserLabel && (
                <InputWithValuePicker
                    id="component-user-label"
                    label={componentConfigurationLabels['USER_LABEL']}
                    testId="user-label-configuration"
                    value={userLabel}
                    onChangeAsValueReference={updateUserLabel}
                    title="Label for the user"
                    container={container}
                />
            )}

            {hasStep && (
                <InputWithValuePicker
                    id="component-step"
                    label={componentConfigurationLabels['STEP']}
                    testId="step-configuration"
                    type="number"
                    value={step}
                    onChangeAsValueReference={updateStep}
                    title="The amount the input changes by when going up/down"
                    container={container}
                />
            )}

            {hasIsSlider && (
                <div className="form-group">
                    <label>
                        <input
                            type="checkbox"
                            checked={isSlider}
                            onChange={() => updateIsSlider(!isSlider ? 'true' : null)}
                        />
                        {componentConfigurationLabels['IS_SLIDER']}
                    </label>
                </div>
            )}

            {hasMask && (
                <>
                    <InputWithValuePicker
                        id="component-masking-pattern"
                        label={componentConfigurationLabels['MASK']}
                        testId="masking-pattern-configuration"
                        value={mask}
                        onChangeAsValueReference={updateMask}
                        title="The masking pattern to be applied to the input"
                        container={container}
                        isRequired
                        validationMessage="A masking pattern must be provided"
                    />
                    <span className="help-block">{translations.PAGE_BUILDER_mask_help_text}</span>
                </>
            )}

            {hasAlertRole && (
                <div className="form-group">
                    <label>
                        <input
                            type="checkbox"
                            checked={alertRole}
                            onChange={() => updateAlertRole(!alertRole ? 'true' : null)}
                        />
                        {componentConfigurationLabels['ALERT_ROLE']}
                    </label>
                </div>
            )}

            {hasVoiceInput && getFlag('VOICE') && (
                <div className="form-group">
                    <label>
                        <input
                            type="checkbox"
                            checked={voiceInput}
                            onChange={() => updateVoiceInput(!voiceInput ? 'true' : null)}
                        />
                        {componentConfigurationLabels['VOICE_INPUT']}
                    </label>
                    <span className="help-block">
                        {translations.PAGE_BUILDER_voice_input_help_text}
                    </span>
                </div>
            )}
        </ConfigSection>
    );
};

export default GeneralSection;
